import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { testimonials, faqs } from '@/mocks/articles';
import { useState } from 'react';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快与您联系！');
      return;
    }
    setFormStatus('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qqpgee3oq4jqumtp4g', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快与您联系！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 国产一区二区精选内容导航平台',
          description: '了解国产一区二区精选内容导航平台的品牌故事、核心优势与联系方式。',
          url: 'https://www.guochanyiqu.com/about',
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Page Header */}
          <section className="pt-20 pb-8 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4">
                关于我们
              </h1>
              <p className="text-sm text-foreground-600 mt-2 max-w-2xl">
                了解国产一区二区精选内容导航平台的品牌故事、核心优势与团队理念
              </p>
            </div>
          </section>

          {/* Brand Introduction */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                    品牌介绍
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      国产一区二区精选内容导航平台创立于2024年，是国内领先的国产优质内容聚合与导航平台。我们致力于为用户提供精准、高效的内容发现体验，通过科学的分类体系和专业的内容筛选，帮助用户在海量信息中快速定位心仪的国产内容资源。
                    </p>
                    <p>
                      平台采用"一区精选"和"二区精选"的双区分类模式，一区精选聚焦影视剧集与动漫动画等视听内容，二区精选涵盖综艺娱乐、纪录片、访谈节目等多元内容形态。两大分区互为补充，全面覆盖国产优质内容的全品类。
                    </p>
                    <p>
                      我们的编辑团队由资深内容从业者组成，每位成员都拥有五年以上的内容行业经验。团队坚持"品质优先、用户至上"的选品理念，每篇推荐文章都经过多轮筛选与深度打磨，确保内容质量始终如一。
                    </p>
                    <p>
                      截至目前，平台已累计收录超过1000+篇精选内容，月活跃用户突破50万人，用户满意度高达99.7%。我们持续追踪国产内容产业的最新动态，保持每日更新的内容节奏，力求成为您最值得信赖的国产内容导航伙伴。
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20creative%20office%20workspace%2C%20warm%20lighting%2C%20team%20collaboration%2C%20minimalist%20design%2C%20wooden%20desk%20with%20plants%2C%20clean%20and%20bright%20atmosphere%2C%20professional%20setting&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="国产一区二区团队办公场景"
                    title="国产一区二区 - 我们的团队"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Core Values */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                核心优势
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    icon: 'ri-stack-line',
                    title: '精准分类',
                    desc: '一区二区清晰划分，多级分类体系帮助用户快速定位目标内容类型。',
                  },
                  {
                    icon: 'ri-shield-check-line',
                    title: '品质保证',
                    desc: '专业编辑团队严格筛选，每篇内容都经过多轮审核，质量有保障。',
                  },
                  {
                    icon: 'ri-flashlight-line',
                    title: '极速更新',
                    desc: '每日持续追踪国产内容最新动态，第一时间为您呈现精彩推荐。',
                  },
                  {
                    icon: 'ri-heart-pulse-line',
                    title: '用户至上',
                    desc: '以用户体验为核心，持续优化平台功能，打造流畅舒适的内容浏览体验。',
                  },
                ].map((item) => (
                  <div key={item.title} className="p-5 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-2xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-10 md:py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">
                用户口碑
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                来自真实用户的评价与反馈
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="p-6 bg-background-50 rounded-lg border border-background-200"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-amber-400 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      "{t.content}"
                    </p>
                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                        <p className="text-xs text-foreground-500">{t.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Milestones */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                发展历程
              </h2>
              <div className="max-w-2xl mx-auto">
                {[
                  { year: '2024.03', event: '国产一区二区平台正式立项，创始团队组建完成' },
                  { year: '2024.06', event: '平台Beta版上线，首批收录200+篇精选内容' },
                  { year: '2024.09', event: '平台正式版发布，月活跃用户突破10万人' },
                  { year: '2025.01', event: '内容库扩充至500+篇，完成分类体系全面升级' },
                  { year: '2025.06', event: '月活跃用户突破30万人，用户满意度达98.5%' },
                  { year: '2026.01', event: '平台累计内容突破1000+篇，成为国产内容导航领域的标杆平台' },
                  { year: '2026.06', event: '月活跃用户突破50万人，持续引领国产内容发现新体验' },
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 pb-6 relative">
                    {idx < 6 && (
                      <div className="absolute left-[17px] top-8 bottom-0 w-px bg-background-300"></div>
                    )}
                    <div className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center shrink-0 relative z-10">
                      <span className="text-xs font-bold">{idx + 1}</span>
                    </div>
                    <div className="pt-1.5">
                      <span className="text-xs font-semibold text-primary-600">{item.year}</span>
                      <p className="text-sm text-foreground-700 mt-1">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-10 md:py-14">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <details
                    key={idx}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                      <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-950 pr-4">
                        {faq.question}
                      </h4>
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-2xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-2">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 text-center mb-8">
                有任何问题或建议，欢迎随时与我们联系
              </p>
              <form onSubmit={handleContactSubmit} data-readdy-form="true" className="space-y-4">
                <div className="honeypot-wrap">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 *
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 *
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 *
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入咨询主题"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您想咨询的内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'loading'}
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {formStatus === 'loading' ? '提交中...' : '提交留言'}
                </button>
                {formStatus !== 'idle' && (
                  <p
                    className={`text-sm text-center ${
                      formStatus === 'success' ? 'text-accent-600' : 'text-primary-600'
                    }`}
                  >
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}